import HourGlass from "@/components/SVG/HourGlass";
import PersonIcon from "@/components/SVG/PersonIcon";
import Pill from "@/components/SVG/Pill";
import Stethoscope from "@/components/SVG/Stethoscope";
import USMap from "@/components/SVG/USMap";
import { ACCEPTED_STATES_ARRAY, stateNames } from "@/constants/AcceptedStates";
import Link from "next/link";
import posthog from "posthog-js";

interface FeatureCardProps {
    background: string;
    CustomIcon: React.ComponentType;
    title: string;
    description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
    background,
    CustomIcon,
    title,
    description,
}) => (
    <div
        className={`h-full p-4 sm:p-6 lg:p-8 rounded-2xl grid grid-rows-[auto_1fr] overflow-hidden ${background}`}
    >
        <div className="flex justify-between items-start gap-4">
            <h3 className="text-[#333333] dark:text-white text-2xl sm:text-3xl lg:text-4xl font-medium font-satoshi leading-[1.2] min-w-0">
                {title}
            </h3>
            <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 shrink-0">
                <CustomIcon />
            </div>
        </div>
        <p className="text-[#333333]/80 dark:text-gray-300 text-base sm:text-lg font-normal font-space leading-relaxed mt-4">
            {description}
        </p>
    </div>
);

const StatesList = () => {
    const shortStateNames = ACCEPTED_STATES_ARRAY;
    const states = shortStateNames.map((state) => stateNames[state]);

    return (
        <div className="text-white/90 text-base sm:text-lg font-normal font-space leading-relaxed">
            {states.map((state, index) => (
                <span key={state}>
                    <span className="whitespace-normal">{state}</span>
                    {index < states.length - 1 && (
                        <span className="text-white/90"> • </span>
                    )}
                </span>
            ))}
        </div>
    );
};

const FeaturesBenefits = () => {
    return (
        <div className="">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
                {/* Main Content (Left 2 Columns) */}
                <div className="grid gap-4 sm:gap-6 xl:col-span-2">
                    {/* Top row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <FeatureCard
                            background="bg-[#fff5fc] dark:bg-[#2d5873]"
                            CustomIcon={HourGlass}
                            title="Same-day Access"
                            description="Get approved and access treatments as fast as one business day"
                        />
                        <FeatureCard
                            background="bg-[#fae9e4] dark:bg-[#4a3f65]"
                            CustomIcon={Stethoscope}
                            title="Expert Guided"
                            description="Medication assessments are created by clinicians specializing in complex illnesses"
                        />
                    </div>

                    {/* Bottom row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <FeatureCard
                            background="bg-[#fff2e4] dark:bg-[#3d5d4c]"
                            CustomIcon={Pill}
                            title="Affordable Price"
                            description="Starting at $49, our assessment fee is less than most specialty provider copays"
                        />
                        <FeatureCard
                            background="bg-[#e8f5ff] dark:bg-[#2c4a6d]"
                            CustomIcon={PersonIcon}
                            title="Personalized Care"
                            description="Custom treatment plans that adapt to your needs and unique health journey"
                        />
                    </div>
                </div>

                {/* States Card */}
                <div className="flex justify-center xl:block">
                    <div className="bg-[#5c6578] rounded-2xl p-4 sm:p-6 flex flex-col max-w-[600px] w-full xl:h-full">
                        <div className="relative mb-4 sm:mb-6 xl:flex-1 xl:min-h-0">
                            <USMap />
                        </div>
                        <div className="flex flex-col justify-end gap-4">
                            <div className="text-white bg-[#5c6578] text-2xl sm:text-3xl lg:text-4xl font-medium font-satoshi leading-[1.2] break-words">
                                Currently Available In:
                            </div>
                            <StatesList />
                            <Link
                                href="https://rthm.feedbear.com/boards/new-states"
                                onClick={() => {
                                    posthog?.capture(
                                        "more_states_button_clicked",
                                    );
                                }}
                                target="_blank"
                                className="text-white/90 hover:text-white transition-colors text-sm sm:text-base font-normal font-space underline"
                            >
                                More states to come
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeaturesBenefits;
